// Parametric picture frame in the profile of the cad frame TV (cad repo projects/objects/frame_tv/model.py):
// a 22 mm square bezel face, a thin inner step (3 x 1.5 mm), the face 25 mm off the wall, the picture 2.4 mm
// behind the face, and a skirt that stops 6 mm short of the wall so a dark gap (the body's side) shows.
//
// The frame is a stack of rectangular rings (outer skirt -> face -> step -> lip -> mat -> bevel); each pair of
// rings is joined by four trapezoids, so the corners are mitred on the diagonal and only the straight pieces
// change length with the image size. Colours are per vertex: albedo x a fixed shade per face direction (no
// realtime lights) x the light sampled from the wall behind the frame (`tint`). The room's grade (sun, pots,
// accent) is applied by the shared room shading patch on the materials.
//
// Geometry is written in world space into shared builders (one per material), so all frames of a room are a few
// draw calls: frames + mats + gap (vertex colours, one mesh), oak frames (+ grain map, one mesh), contact shadows
// (one mesh), and one quad grid per picture (its own texture). ~110 triangles per frame.

import { BufferAttribute, BufferGeometry, Vector3 } from 'three';
import type { ArtFinish } from '@lr3d/shared';

export type Finish = ArtFinish;

/** Profile, metres (cad frame_tv, mm / 1000). */
export const PROFILE = {
  /** visible bezel face, opening edge to outer edge */
  face: 0.022,
  /** inner step at the opening: width, depth below the face */
  stepW: 0.003,
  stepD: 0.0015,
  /** wall to the bezel face */
  depth: 0.025,
  /** bezel face to the picture plane */
  recess: 0.0024,
  /** the skirt stops this far from the wall */
  shadowGap: 0.006,
  /** skirt thickness: the body's side shows this far inside the outer edge */
  skirt: 0.005,
  /** mat board: thickness (= depth of its bevel), bevel width */
  matT: 0.0015,
  matBevel: 0.0015,
  /** back of the frame off the wall surface */
  hang: 0.0005,
};

/** Albedo (linear RGB): oak and black_matte as the cad materials (black slightly lifted); the white is whiter than
 * cad frame_warm_white (0.80, 0.76, 0.68), which blended into the wall. */
export const FINISH_ALBEDO: Record<Exclude<Finish, 'custom'>, [number, number, number]> = {
  warm_white: [0.95, 0.945, 0.93], // a clean white, clearly lighter than the wall paint (0.80, 0.785, 0.75)
  oak: [0.30, 0.18, 0.085], // a honey oak between the speaker wall and the floor strips (user: lighter, closer to the floor)
  black: [0.035, 0.034, 0.033],
};
// light grey-beige board, clearly darker than the frame (sRGB #dad8d4 = ART_MAT_DEFAULT; the panel's default mat dot)
export const MAT_ALBEDO: [number, number, number] = [0.7, 0.69, 0.66];
const CORE_ALBEDO: [number, number, number] = [0.96, 0.955, 0.94]; // the mat's bevel shows the white core
const BODY_ALBEDO: [number, number, number] = [0.02, 0.02, 0.02]; // body side in the shadow gap
/** The picture sits a little deeper in the frame than the face: darkened slightly. */
export const PICTURE_GAIN = 0.92;

export interface FrameSpec {
  /** image width / height */
  aspect: number;
  /** long side of the outer frame (m) */
  size: number;
  finish: Finish;
  /** albedo (linear RGB) of a 'custom' (painted) frame; missing = the white */
  color?: [number, number, number];
  /** mat width (m), 0 = none */
  mat: number;
  /** mat board albedo (linear RGB); missing = the default grey-beige (MAT_ALBEDO) */
  matColor?: [number, number, number];
  /** back of the frame off the wall surface (m), default PROFILE.hang; the large frame hangs over the mouldings */
  hang?: number;
}

export interface FrameLayout {
  outerW: number;
  outerH: number;
  /** picture (image) size */
  imageW: number;
  imageH: number;
}

/** `#rrggbb` (sRGB) -> linear RGB albedo. */
export function hexToLinear(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  const lin = (v: number) => { const c = v / 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  return [lin((n >> 16) & 255), lin((n >> 8) & 255), lin(n & 255)];
}

export function frameLayout(s: FrameSpec): FrameLayout {
  const border = 2 * (PROFILE.face + s.mat);
  const a = Math.max(0.05, s.aspect);
  let imageW: number, imageH: number;
  if (a >= 1) {
    imageW = Math.max(0.02, s.size - border);
    imageH = imageW / a;
  } else {
    imageH = Math.max(0.02, s.size - border);
    imageW = imageH * a;
  }
  return { outerW: imageW + border, outerH: imageH + border, imageW, imageH };
}

/** Where a frame hangs: centre on the wall surface, and the wall's axes (unit vectors, world). */
export interface Placement {
  origin: Vector3;
  right: Vector3;
  up: Vector3;
  normal: Vector3;
}

export function placement(position: Vector3, normal: Vector3): Placement {
  const n = normal.clone().normalize();
  // a floor or ceiling slot (the Basement's overlay slot, 2026-10-30): "up" on the surface is -Z (north)
  const up = Math.abs(n.y) > 0.9 ? new Vector3(0, 0, -1) : new Vector3(0, 1, 0);
  up.addScaledVector(n, -up.dot(n)).normalize(); // walls are vertical; keep it exact anyway
  const right = new Vector3().crossVectors(up, n).normalize(); // the viewer's right, facing the wall
  return { origin: position.clone(), right, up, normal: n };
}

/** Light at a point of the frame (local x right, y up from the centre, m) as a linear RGB multiplier. */
export type TintFn = (x: number, y: number, out: [number, number, number]) => void;

/**
 * A weaker version of the wall light for the frame and mat, so they read whiter than the wall: the brightness
 * goes to the power 0.45 (0.7 -> 0.85, 0.5 -> 0.73) and a third of the colour cast stays. The picture keeps the full
 * light.
 */
export function softTint(tint: TintFn): TintFn {
  return (x, y, out) => {
    tint(x, y, out);
    const l = Math.max(1e-4, 0.2126 * out[0] + 0.7152 * out[1] + 0.0722 * out[2]);
    const k = Math.pow(l, 0.45);
    for (let c = 0; c < 3; c++) out[c] = k * (0.65 + 0.35 * (out[c]! / l));
  };
}

/** Growable world-space geometry with colour (and uv) per vertex. */
export class GeoBuilder {
  pos: number[] = [];
  col: number[] = [];
  uv: number[] = [];
  idx: number[] = [];
  get vertexCount(): number { return this.pos.length / 3; }
  get triangles(): number { return this.idx.length / 3; }
  vertex(p: Vector3, c: ArrayLike<number>, u = 0, v = 0): number {
    this.pos.push(p.x, p.y, p.z);
    this.col.push(c[0]!, c[1]!, c[2]!);
    this.uv.push(u, v);
    return this.vertexCount - 1;
  }
  /** Append another builder's geometry. */
  append(o: GeoBuilder): void {
    const base = this.vertexCount;
    for (let i = 0; i < o.pos.length; i++) this.pos.push(o.pos[i]!);
    for (let i = 0; i < o.col.length; i++) this.col.push(o.col[i]!);
    for (let i = 0; i < o.uv.length; i++) this.uv.push(o.uv[i]!);
    for (let i = 0; i < o.idx.length; i++) this.idx.push(o.idx[i]! + base);
  }
  build(uv: boolean): BufferGeometry | null {
    if (!this.idx.length) return null;
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(new Float32Array(this.pos), 3));
    g.setAttribute('color', new BufferAttribute(new Float32Array(this.col), 3));
    if (uv) g.setAttribute('uv', new BufferAttribute(new Float32Array(this.uv), 2));
    const n = this.vertexCount;
    g.setIndex(new BufferAttribute(n > 65535 ? new Uint32Array(this.idx) : new Uint16Array(this.idx), 1));
    g.computeBoundingSphere();
    g.computeBoundingBox();
    return g;
  }
}

export interface FrameTargets {
  /** vertex-coloured, no texture: warm white and black bars, mats, bevels, the gap */
  plain: GeoBuilder;
  /** oak bars (grain map) */
  oak: GeoBuilder;
  /** contact shadows (multiplied over the wall) */
  shadow: GeoBuilder;
}

/** A rectangle ring: half extents inset from the outer edge, distance from the wall. */
interface Ring { inset: number; z: number }

const _p = new Vector3();
const _n = new Vector3();
const _e1 = new Vector3();
const _e2 = new Vector3();
const _tint: [number, number, number] = [1, 1, 1];
const _c: [number, number, number] = [0, 0, 0];

/** The window side of the room (+X in the listening room): faces turned that way get a little more light. */
const WINDOW_DIR = new Vector3(1, 0, 0);

/**
 * Fixed shade for a face direction (unlit): front faces 1, faces turned up (toward the ceiling and pot lights)
 * ~1, sideways 0.8, faces turned down 0.6, plus a little toward the windows.
 */
function shade(nWorld: Vector3, wallN: Vector3): number {
  return Math.min(1.05, Math.max(0.45, 0.8 + 0.2 * nWorld.y + 0.2 * nWorld.dot(wallN) + 0.06 * nWorld.dot(WINDOW_DIR)));
}

/**
 * Write one frame (bars, step, optional mat, the gap, contact shadow) into the builders and return the picture
 * plane's distance from the wall surface.
 */
export function buildFrame(spec: FrameSpec, layout: FrameLayout, pl: Placement, tint: TintFn, t: FrameTargets): { z: number } {
  const P = PROFILE;
  const hx = layout.outerW / 2, hy = layout.outerH / 2;
  const h = spec.hang ?? P.hang;
  const zFace = h + P.depth;
  const zPic = zFace - P.recess;
  const world = (x: number, y: number, z: number, out: Vector3) =>
    out.copy(pl.origin).addScaledVector(pl.right, x).addScaledVector(pl.up, y).addScaledVector(pl.normal, z);

  /**
   * Join two rings with four trapezoids. `facing`: 'front' (toward the room), 'out' (away from the centre) or
   * 'in' (toward the centre); picks the winding. `occl`: extra darkening for faces in a corner (the step,
   * the lip). Oak strips get uvs: u along the bar, v across it.
   */
  const strip = (a: Ring, b: Ring, albedo: readonly number[], facing: 'front' | 'out' | 'in', target: GeoBuilder, occl = 1) => {
    // sides: bottom, right, top, left; each as (outward 2D direction, along axis)
    for (let s = 0; s < 4; s++) {
      const ox = s === 1 ? 1 : s === 3 ? -1 : 0;
      const oy = s === 0 ? -1 : s === 2 ? 1 : 0;
      // corner points of ring r on side s: start and end along the side (counter-clockwise seen from the front)
      const corner = (r: Ring, end: boolean): [number, number] => {
        const ex = hx - r.inset, ey = hy - r.inset;
        const k = end ? 1 : -1;
        // walk along the side: bottom -> +x, right -> +y, top -> -x, left -> -y
        if (s === 0) return [k * ex, -ey];
        if (s === 1) return [ex, k * ey];
        if (s === 2) return [-k * ex, ey];
        return [-ex, -k * ey];
      };
      const q = [corner(a, false), corner(a, true), corner(b, true), corner(b, false)];
      const zs = [a.z, a.z, b.z, b.z];
      const pts = q.map(([x, y], i) => world(x, y, zs[i]!, new Vector3()));
      // geometric normal of (0, 1, 2)
      _e1.subVectors(pts[1]!, pts[0]!);
      _e2.subVectors(pts[2]!, pts[0]!);
      _n.crossVectors(_e1, _e2).normalize();
      // desired direction
      _p.set(0, 0, 0);
      if (facing === 'front') _p.copy(pl.normal);
      else _p.addScaledVector(pl.right, ox).addScaledVector(pl.up, oy).multiplyScalar(facing === 'out' ? 1 : -1);
      const flip = _n.dot(_p) < 0;
      if (flip) _n.negate();
      // bevelled strips (inset and z both change) take the true normal; flat or upright ones the desired one
      const sh = shade(_n, pl.normal) * occl;
      const base = target.vertexCount;
      for (let i = 0; i < 4; i++) {
        const [x, y] = q[i]!;
        tint(x, y, _tint);
        for (let c = 0; c < 3; c++) _c[c] = albedo[c]! * sh * _tint[c]!;
        const along = s === 0 || s === 2 ? x : y;
        const across = (s === 0 || s === 2 ? y : x) + zs[i]!;
        target.vertex(pts[i]!, _c, along / 0.45, across / 0.03);
      }
      if (flip) target.idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
      else target.idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
    }
  };

  const bars = spec.finish === 'oak' ? t.oak : t.plain;
  const fin = spec.finish === 'custom' ? spec.color ?? FINISH_ALBEDO.warm_white : FINISH_ALBEDO[spec.finish];
  const R = {
    skirtBack: { inset: 0, z: h + P.shadowGap },
    outer: { inset: 0, z: zFace },
    stepOuter: { inset: P.face - P.stepW, z: zFace },
    stepRiser: { inset: P.face - P.stepW, z: zFace - P.stepD },
    tread: { inset: P.face, z: zFace - P.stepD },
    opening: { inset: P.face, z: zPic },
  };
  strip(R.skirtBack, R.outer, fin, 'out', bars); // outer skirt
  strip(R.outer, R.stepOuter, fin, 'front', bars); // face
  strip(R.stepOuter, R.stepRiser, fin, 'in', bars, 0.9); // step riser
  strip(R.stepRiser, R.tread, fin, 'front', bars, 0.82); // step tread, in the riser's shadow
  strip(R.tread, R.opening, fin, 'in', bars, 0.7); // lip down to the picture plane
  let z = zPic;
  if (spec.mat > 0) {
    const m0 = { inset: P.face + spec.mat - P.matBevel, z: zPic };
    const m1 = { inset: P.face + spec.mat, z: zPic - P.matT };
    strip(R.opening, m0, spec.matColor ?? MAT_ALBEDO, 'front', t.plain);
    strip(m0, m1, CORE_ALBEDO, 'in', t.plain);
    z = zPic - P.matT;
  }
  // the body's side in the shadow gap (dark)
  strip({ inset: P.skirt, z: h }, { inset: P.skirt, z: h + P.shadowGap + 0.001 }, BODY_ALBEDO, 'out', t.plain);

  // a frame hung off the wall (over the mouldings) casts its shadow just behind its back, in front of the beads
  contactShadow(layout, pl, t.shadow, world, Math.max(SHADOW.z, h - 0.0005));
  return { z };
}

/** Contact shadow, metres: blur half-width round the frame edge, offset (down, and away from the windows). */
export const SHADOW = { r: 0.035, down: 0.016, side: 0.007, z: 0.0002 };

/**
 * Nine-slice quad on the wall round the frame: the corners keep their size (the shared texture's corners), only
 * the straight middle parts stretch. u, v run 0 -> 0.5 across the left / bottom band and 0.5 -> 1 across the
 * right / top band; the texture is dark where both are near 0.5.
 */
function contactShadow(layout: FrameLayout, pl: Placement, out: GeoBuilder, world: (x: number, y: number, z: number, o: Vector3) => Vector3, z: number): void {
  const S = SHADOW;
  // shift away from the windows along the wall (the side of the frame away from the light)
  const side = -Math.sign(pl.right.dot(WINDOW_DIR)) * S.side;
  const hx = layout.outerW / 2, hy = layout.outerH / 2;
  const xs = [-hx - S.r, -hx + S.r, hx - S.r, hx + S.r].map((x) => x + side);
  const ys = [-hy - S.r, -hy + S.r, hy - S.r, hy + S.r].map((y) => y - S.down);
  const us = [0, 0.5, 0.5, 1];
  const base = out.vertexCount;
  const white = [1, 1, 1];
  for (let j = 0; j < 4; j++) for (let i = 0; i < 4; i++) out.vertex(world(xs[i]!, ys[j]!, z, new Vector3()), white, us[i]!, us[j]!);
  for (let j = 0; j < 3; j++) for (let i = 0; i < 3; i++) {
    const a = base + j * 4 + i;
    out.idx.push(a, a + 1, a + 5, a, a + 5, a + 4);
  }
  // winding: right x up = normal, so (a, a+1, a+5) is counter-clockwise seen from the room
}

/**
 * The picture: a grid of quads (so the wall light can vary across it), uv 0..1 (v up), at distance z from the
 * wall. Returned as its own geometry (each picture has its own texture).
 */
export function buildPicture(layout: FrameLayout, pl: Placement, z: number, tint: TintFn, cells = 4): BufferGeometry {
  const b = new GeoBuilder();
  const w = layout.imageW, hgt = layout.imageH;
  const p = new Vector3();
  for (let j = 0; j <= cells; j++) for (let i = 0; i <= cells; i++) {
    const u = i / cells, v = j / cells;
    const x = (u - 0.5) * w, y = (v - 0.5) * hgt;
    tint(x, y, _tint);
    for (let c = 0; c < 3; c++) _c[c] = PICTURE_GAIN * _tint[c]!;
    p.copy(pl.origin).addScaledVector(pl.right, x).addScaledVector(pl.up, y).addScaledVector(pl.normal, z);
    b.vertex(p, _c, u, v);
  }
  const row = cells + 1;
  for (let j = 0; j < cells; j++) for (let i = 0; i < cells; i++) {
    const a = j * row + i;
    b.idx.push(a, a + 1, a + row + 1, a, a + row + 1, a + row);
  }
  return b.build(true)!;
}
